import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SimpleContactFormat } from '@/components/persons/SimpleContactFormat';
import UnifiedFamilySection from '@/components/persons/UnifiedFamilySection';
import { CategorySection } from '@/components/persons/CategorySection';
import { PersonsGrid } from '@/components/persons/PersonsGrid';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { usePersonProfiles, PersonProfile } from '@/hooks/usePersonProfiles';
import { useFamily } from '@/hooks/useFamily';
import { useAuth } from '@/hooks/useAuth';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { calculateAge } from '@/lib/utils';
import { Users, Plus, AlertTriangle, Heart, Baby, UserPlus, Stethoscope } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import MobilePersons from './MobilePersons';

type FilterType = 'parents' | 'enfants' | 'famille_etendue' | 'amis' | 'medical' | 'urgences' | null;

export default function Persons() {
  const { user } = useAuth();
  const { family, familyMembers } = useFamily();
  const { persons, loading, createPerson, updatePerson, deletePerson, refreshPersons } = usePersonProfiles(family?.id);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { openModal } = useModalNavigation();
  
  const [deletingPersonId, setDeletingPersonId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>(null);

  const location = useLocation();
  useEffect(() => {
    // Rafraîchit la liste quand on revient sur cet écran
    refreshPersons();
  }, [location.pathname]);

  // Get counts for each category - include ALL parent roles
  const parentPersons = persons.filter(p => p.category === 'family' && (p.family_role === 'father' || p.family_role === 'mother' || p.family_role === 'parent' || p.family_role === 'spouse'));
  const childPersons = persons.filter(p => p.category === 'family' && (p.family_role === 'child' || p.family_role === 'son' || p.family_role === 'daughter'));
  const allFamilyMembers = familyMembers?.filter(member => 
    ['father', 'mother', 'parent', 'spouse', 'child', 'son', 'daughter'].includes(member.role)
  ) || [];
  const extendedFamily = persons.filter(p => p.category === 'extended_family');
  const friends = persons.filter(p => p.category === 'friends');
  const services = persons.filter(p => p.category === 'services');
  const medical = persons.filter(p => p.category === 'medical_other');
  const emergencyContacts = persons.filter(p => p.emergency_contact);

  // Calculate total counts including both persons and family members
  const parentsCount = parentPersons.length + (familyMembers?.filter(m => ['father', 'mother', 'parent', 'spouse'].includes(m.role))?.length || 0);
  const childrenCount = childPersons.length + (familyMembers?.filter(m => ['child', 'son', 'daughter'].includes(m.role))?.length || 0);

  const handlePersonEdit = (person: PersonProfile) => {
    navigate(`/persons/edit/${person.id}`);
  };

  const handlePersonDelete = async (personId: string) => {
    await deletePerson(personId);
    setDeletingPersonId(null);
  };

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const handleFilterClick = (filterType: FilterType) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!family) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune famille</h3>
            <p className="text-muted-foreground">Vous devez d'abord créer ou rejoindre une famille.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Use mobile component on mobile devices
  if (isMobile) {
    return <MobilePersons />;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header avec bouton + */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Personnes</h1>
        <Button size="sm" onClick={() => openModal('createPerson', { category: 'family' })} style={{ backgroundColor: 'hsl(var(--accent))' }}>
          <Plus className="h-5 w-5" />
        </Button>
      </div>

      {/* Filtres cliquables */}
      <div className="grid grid-cols-2 gap-3">
        {/* Parents */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeFilter === 'parents' ? 'ring-2 ring-primary bg-primary/5' : ''
          }`}
          onClick={() => handleFilterClick('parents')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Parents</div>
                <div className="text-sm text-muted-foreground">{parentsCount}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Enfants */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeFilter === 'enfants' ? 'ring-2 ring-primary bg-primary/5' : ''
          }`}
          onClick={() => handleFilterClick('enfants')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Baby className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Enfants</div>
                <div className="text-sm text-muted-foreground">{childrenCount}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Famille étendue */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeFilter === 'famille_etendue' ? 'ring-2 ring-primary bg-primary/5' : ''
          }`}
          onClick={() => handleFilterClick('famille_etendue')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Heart className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Famille étendue</div>
                <div className="text-sm text-muted-foreground">{extendedFamily.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Amis */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeFilter === 'amis' ? 'ring-2 ring-primary bg-primary/5' : ''
          }`}
          onClick={() => handleFilterClick('amis')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserPlus className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Amis</div>
                <div className="text-sm text-muted-foreground">{friends.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Médical/Autres */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeFilter === 'medical' ? 'ring-2 ring-primary bg-primary/5' : ''
          }`}
          onClick={() => handleFilterClick('medical')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Stethoscope className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Médical/Autres</div>
                <div className="text-sm text-muted-foreground">{medical.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contacts d'urgence */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeFilter === 'urgences' ? 'ring-2 ring-destructive bg-destructive/5' : ''
          }`}
          onClick={() => handleFilterClick('urgences')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Urgences</div>
                <div className="text-sm text-muted-foreground">{emergencyContacts.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contenu */}
      {activeFilter === 'parents' && (
        <div className="space-y-4">
          <UnifiedFamilySection
            familyId={family.id}
            persons={parentPersons}
            familyMembers={familyMembers}
            onRefresh={refreshPersons}
            onPersonCreate={createPerson}
            onPersonUpdate={updatePerson}
            onPersonDelete={deletePerson}
          />
          {parentsCount === 0 && (
            <p className="text-muted-foreground text-center py-8">
              Aucun parent ajouté.
            </p>
          )}
        </div>
      )}

      {activeFilter === 'enfants' && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Enfants</h3>
          <div className="space-y-2">
            {childPersons.map((child) => (
              <SimpleContactFormat
                key={child.id}
                person={child}
                onEdit={handlePersonEdit}
                onDelete={(personId) => setDeletingPersonId(personId)}
                onUpdate={refreshPersons}
              />
            ))}
            {familyMembers?.filter(m => m.role === 'child' || m.role === 'son' || m.role === 'daughter').map((child) => (
              <div key={child.id} className="flex items-center gap-3 py-2">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Baby className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{child.child_name}</span>
                  </div>
                  {child.child_birth_date && (
                    <p className="text-xs text-muted-foreground">
                      {calculateAge(child.child_birth_date)} ans
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {childrenCount === 0 && (
            <p className="text-muted-foreground text-center py-8">
              Aucun enfant ajouté.
            </p>
          )}
        </div>
      )}

      {activeFilter === 'famille_etendue' && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Famille étendue</h3>
          {extendedFamily.length > 0 ? (
            <div className="space-y-2">
              {extendedFamily.map((person) => (
                <SimpleContactFormat
                  key={person.id}
                  person={person}
                  onEdit={handlePersonEdit}
                  onDelete={(personId) => setDeletingPersonId(personId)}
                  onUpdate={refreshPersons}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Aucun membre de la famille étendue ajouté.
            </p>
          )}
        </div>
      )}

      {activeFilter === 'amis' && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Amis</h3>
          {friends.length > 0 ? (
            <div className="space-y-2">
              {friends.map((person) => (
                <SimpleContactFormat
                  key={person.id}
                  person={person}
                  onEdit={handlePersonEdit}
                  onDelete={(personId) => setDeletingPersonId(personId)}
                  onUpdate={refreshPersons}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Aucun ami ajouté.
            </p>
          )}
        </div>
      )}

      {activeFilter === 'medical' && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Médical / Autres professionnels</h3>
          {medical.length > 0 ? (
            <div className="space-y-2">
              {medical.map((person) => (
                <SimpleContactFormat
                  key={person.id}
                  person={person}
                  onEdit={handlePersonEdit}
                  onDelete={(personId) => setDeletingPersonId(personId)}
                  onUpdate={refreshPersons}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Aucun professionnel médical ajouté.
            </p>
          )}
        </div>
      )}

      {activeFilter === 'urgences' && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Contacts d'urgence</h3>
          {emergencyContacts.length > 0 ? (
            <div className="space-y-2">
              {emergencyContacts.map((contact) => (
                <SimpleContactFormat
                  key={contact.id}
                  person={contact}
                  onEdit={handlePersonEdit}
                  onDelete={(personId) => setDeletingPersonId(personId)}
                  onUpdate={refreshPersons}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Aucun contact d'urgence configuré.
            </p>
          )}
        </div>
      )}

      {/* Affichage de tous les contacts quand aucun filtre n'est sélectionné */}
      {activeFilter === null && (
        <div className="space-y-8">
          {/* Famille */}
          <CategorySection
            title="Famille"
            icon="👨‍👩‍👧‍👦"
            persons={[...parentPersons, ...childPersons]}
          />

          {/* Famille étendue */}
          <CategorySection
            title="Famille étendue"
            icon="👪"
            persons={extendedFamily}
          />

          {/* Amis */}
          <CategorySection
            title="Amis"
            icon="👥"
            persons={friends}
          />

          {/* Services */}
          <CategorySection
            title="Services"
            icon="🛠️"
            persons={services}
          />

          {/* Médical/Autres */}
          <CategorySection
            title="Médical/Autres"
            icon="🏥"
            persons={medical}
          />

          {/* Contacts d'urgence */}
          <CategorySection
            title="Contacts d'urgence"
            icon="🚨"
            persons={emergencyContacts}
          />

          {/* Empty state */}
          {persons.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Vous n'avez pas encore ajouté de personnes
              </p>
              <Button onClick={() => openModal('createPerson')} size="lg">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une personne
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingPersonId} onOpenChange={() => setDeletingPersonId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette personne ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La personne sera définitivement supprimée de votre carnet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => deletingPersonId && handlePersonDelete(deletingPersonId)}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}